import React, { useEffect, useState, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { PropTypes } from 'prop-types';
import injectSheet from 'react-jss';
import { Loading, Button } from '@spoiler-alert/ui-library';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import DataTableManagerNavigation from './dtm-navigation';
import NewDataTableProfile from './new-data-table-profile';
import DataTableColumnsTable from './data-table-columns-table';
import {
  CUSTOMERS_AND_DATA_TABLE_PROFILES,
  DATA_TABLE_PROFILE_FIELDS_QUERY,
  SAVE_DATA_TABLE_PROFILE_MUTATION,
} from './dtm-queries';
import ETLImportErrors from '../import-manager/etl-import-errors';
import ProfileUploadConfirmationModal from '../../components/modals/profile-upload-confirmation-modal';
import useProfileTransfer from '../../lib/profile-transfer/use-profile-transfer';
import { buildDataTableProfileDocument } from '../../lib/profile-transfer/serialize';
import { PROMOTE_DATA_TABLE_PROFILE_MUTATION } from '../../lib/profile-transfer/profile-transfer-queries';
import { PROFILE_KINDS } from '../../lib/profile-transfer/profile-document';
import AppSettings from '../../app-settings';

const styles = {
  wrap: {
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 25,
    alignItems: 'center',
  },
  saveWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  validation: {
    marginRight: 25,
  },
  pageButtons: {
    display: 'flex',
    flexDirection: 'row',
    '&>button': {
      marginLeft: 10,
    },
  },
};

const DataTableManager = ({ classes }) => {
  const [site, setSite] = useState({ _id: null, siteName: null });
  const [tableType, setTableType] = useState(null);
  const [matchingReferenceProfiles, setMatchingReferenceProfiles] = useState([]);
  const [referenceProfile, setReferenceProfile] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [errorList, setErrorList] = useState([]);
  const editingColumns = useRef(null);

  // queries and mutations
  const { data: headerData } = useQuery(CUSTOMERS_AND_DATA_TABLE_PROFILES);
  const { data: profileFields, loading: loadingFields } = useQuery(DATA_TABLE_PROFILE_FIELDS_QUERY, {
    variables: {
      dataTableProfileId: referenceProfile?.id,
    },
    skip: !referenceProfile,
    fetchPolicy: 'no-cache',
  });
  const [saveProfile, { data: saveProfileData, loading: saveProfileLoading, error: saveProfileError }] = useMutation(
    SAVE_DATA_TABLE_PROFILE_MUTATION,
  );

  const sites = headerData?.dataTableManagerQuery?.allSellerOrgs;
  const tableTypes = headerData?.dataTableManagerQuery?.possibleTableTypes;
  const existingDataTableProfiles = headerData?.dataTableManagerQuery?.dataTableProfiles;

  useEffect(() => {
    TitleService.setTitles('Data Table Manager');
    Breadcrumbs.set([
      {
        url: '/table-manager',
        title: 'Data Table Manager',
      },
    ]);
  });

  useEffect(() => {
    if (site?._id && tableType && existingDataTableProfiles) {
      const matchingProfile = existingDataTableProfiles.find(
        (p) => p.siteId === site._id && p.dataTableName === tableType,
      );
      setActiveProfile(matchingProfile);
      setReferenceProfile(matchingProfile);
      setMatchingReferenceProfiles(existingDataTableProfiles.filter((p) => p.dataTableName === tableType));
    } else {
      setActiveProfile(null);
      setMatchingReferenceProfiles([]);
      setReferenceProfile(null);
    }
  }, [site, tableType, existingDataTableProfiles]);

  useEffect(() => {
    if (saveProfileData) {
      setActiveProfile(saveProfileData.SaveDataTableProfile);
      setReferenceProfile(saveProfileData.SaveDataTableProfile);
    }
  }, [saveProfileData]);

  const save = () => {
    saveProfile({
      variables: {
        dataTableProfile: {
          id: activeProfile?.id,
          siteName: site.siteName,
          siteId: site._id,
          dataTableName: tableType,
          columns: editingColumns.current?.map((c) => {
            c.__typename = undefined;
            c.key = undefined;
            return c;
          }),
          dataTableType: referenceProfile.dataTableType,
        },
      },
      refetchQueries: ['getSitesAndDataTableProfiles'],
    });
  };

  const { download, requestUpload, pendingUpload, confirmUpload, cancelUpload, uploading, fileInputProps } =
    useProfileTransfer({
      kind: PROFILE_KINDS.DATA_TABLE,
      identity: { dataTableName: tableType },
      // siteName as well as siteId: the profile schema requires it, and it must come from the
      // selection. Downloading the default table and uploading it here is the supported way to
      // stand up a site's profile, so the document's own site is deliberately ignored.
      target: { siteId: site._id, siteName: site.siteName, dataTableName: tableType },
      buildDocument: (exportedAt) =>
        buildDataTableProfileDocument({
          environment: AppSettings.ENVIRONMENT_NAME,
          dataTableProfile: profileFields?.dataTableManagerQuery?.dataTableProfile,
          exportedAt,
        }),
      mutation: PROMOTE_DATA_TABLE_PROFILE_MUTATION,
      resultKey: 'promoteDataTableProfile',
      refetchQueries: ['getSitesAndDataTableProfiles', 'getColumnsByDataTableProfileId'],
      onPromoted: (result) => {
        setActiveProfile(result.profile);
        setReferenceProfile(result.profile);
      },
    });

  const tableLoading = () => loadingFields;

  const renderView = () => {
    if (!tableType) {
      return <div />;
    }
    if (tableType != null && !referenceProfile) {
      return (
        <NewDataTableProfile
          site={site}
          tableType={tableType}
          possibleDataTableProfiles={matchingReferenceProfiles}
          referenceProfile={referenceProfile}
          setReferenceProfile={setReferenceProfile}
        />
      );
    }
    if (referenceProfile) {
      return (
        <div>
          <DataTableColumnsTable
            possibleFields={profileFields?.dataTableManagerQuery.dataTableProfile.possibleFields}
            columns={profileFields?.dataTableManagerQuery.dataTableProfile.columns}
            loading={loadingFields}
            editingColumns={editingColumns}
            tableType={tableType}
            setErrorList={setErrorList}
          />
        </div>
      );
    }
    return <div />;
  };

  if (saveProfileError)
    return `Something went wrong when saving. Please contact engineering. ${saveProfileError.message}`;
  return (
    <div className={classes.wrap}>
      <header className={classes.header}>
        <DataTableManagerNavigation
          sites={sites}
          site={site}
          setSite={setSite}
          tableTypes={tableTypes}
          tableType={tableType}
          setTableType={setTableType}
        />
        <div className={classes.saveWrap}>
          <div className={classes.validation}>
            <ETLImportErrors showCount={true} showLabel={true} errors={errorList} />
          </div>
          <div className={classes.pageButtons}>
            <Button primary onClick={requestUpload} disabled={errorList.length > 0 || uploading || loadingFields}>
              Upload Profile
            </Button>
            {referenceProfile && (
              <>
                <Button primary onClick={download} disabled={errorList.length > 0 || loadingFields}>
                  Download Profile
                </Button>
                <Button
                  primary
                  onClick={save}
                  disabled={errorList.length > 0}
                  loading={saveProfileLoading}
                  loadingText={'Saving'}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      {/* Mounted unconditionally: requestUpload() clicks this hidden input, so it must exist even
          when no profile is selected -- which is exactly when you would import one. The modal
          renders nothing until there is a pending upload. */}
      <ProfileUploadConfirmationModal
        pendingUpload={pendingUpload}
        targetDescription={`${site.siteName} — ${tableType}`}
        onConfirm={confirmUpload}
        onCancel={cancelUpload}
        uploading={uploading}
      />
      <input {...fileInputProps} />
      <Loading loading={tableLoading()}>{renderView()}</Loading>
    </div>
  );
};

DataTableManager.propTypes = {
  classes: PropTypes.object,
};

export default injectSheet(styles)(DataTableManager);
